/*
 * THE NOTIFICATIONS FEED (docs/design/stage3-alerts.md §4.1).
 *
 * Why a dedicated screen exists at all, since the question was asked and
 * answered rather than assumed (§4.1): the three data views show CURRENT STATE,
 * and four things need a home that is not one of them — the durable record ("did
 * it tell me?", answerable only from a list of what it said, with timestamps),
 * read state, the landing surface every future email link needs, and the
 * clock-driven alerts, which have nowhere else to live because Upcoming already
 * shows the deadline statically and "3 days left" is a different thing from a
 * date on a band.
 *
 * REJECTED AND NOT TO BE RE-LITIGATED (§4.1): inline "new since" markers alone,
 * which give no history and no landing surface; and a bottom tab bar, which the
 * shell rules out by name for web.
 *
 * WHAT THE SERVER DECIDED AND WHAT THIS SCREEN DECIDES. The server owns "is this
 * unread" — it holds the watermark and computes supersession, and decision 400
 * requires the count and the styling to be ONE predicate, so a client that
 * re-derived either would be the second predicate that rule exists to prevent.
 * This screen owns every word (lib/alertText.ts), the day grouping
 * (lib/alertGroups.ts) and whether the "New" marker renders at all.
 *
 * OPENING THE FEED ADVANCES THE WATERMARK (§4.1), and the rows keep the styling
 * they arrived with. That is deliberate: marking seen on mount and then
 * re-reading would make every row lose its "New" marker under the reader's eyes,
 * which is the one moment they most want to see which lines are new. The badge
 * clears; the page does not.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Ago } from "../components/Ago";
import { ApiError, api, list, type Alert, type AlertsPayload } from "../lib/api";
import { alertDestination, groupByDay } from "../lib/alertGroups";
import { alertSentence, attributionSource, dayHeading, teamMembers } from "../lib/alertText";
import { withTableName } from "../lib/deTable";
import type { NotificationNameFormat } from "../lib/format";
import styles from "./Notifications.module.css";

export interface NotificationsProps {
  /**
   * How a TRACKED fencer is named in this feed's copy (decision 535), from the
   * account's own preference.
   *
   * Threaded in from the route rather than read here: the screen takes no user
   * object, and asking `/api/me` for one field would be a second read of state
   * the shell already holds. Defaulted to "full", which is what every row
   * rendered before the setting existed.
   */
  nameFormat?: NotificationNameFormat;
  /** Refreshes the shell's badge after a read-state change, without a reload. */
  onUnreadCountChanged: (count: number) => void;
  /** Injectable for tests, so "Today" does not depend on the day the suite runs. */
  now?: Date;
}

export function Notifications({
  nameFormat = "full",
  onUnreadCountChanged,
  now,
}: NotificationsProps) {
  const { t, i18n } = useTranslation();

  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [cursor, setCursor] = useState<{ observedAt: string; id: string } | null>(null);
  const [newest, setNewest] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  /** The last dismissal, for the one-step undo. */
  const [undoable, setUndoable] = useState<Alert | null>(null);

  const absorb = useCallback((payload: AlertsPayload, append: boolean) => {
    const page = list(payload.alerts);
    setAlerts((prev) => (append && prev ? [...prev, ...page] : page));
    setCursor(
      payload.next_before_observed_at && payload.next_before_id
        ? { observedAt: payload.next_before_observed_at, id: payload.next_before_id }
        : null,
    );
    // Only the FIRST page carries the newest instant. A later page's rows are
    // older by construction, and taking its head would walk the watermark
    // backwards — which the server clamps anyway, but sending a value that means
    // something else is how a clamp comes to be load-bearing.
    if (!append) setNewest(payload.newest_observed_at ?? null);
  }, []);

  // The first page, and the watermark.
  useEffect(() => {
    let cancelled = false;

    api
      .alerts()
      .then(async (payload) => {
        if (cancelled) return;
        absorb(payload, false);

        // OPENING THE FEED IS READING IT. The instant comes from the payload and
        // not from the clock, so an alert written between the fetch and this call
        // is not marked read — see api.markAlertsSeen.
        if (payload.newest_observed_at) {
          await api.markAlertsSeen(payload.newest_observed_at).catch(() => {
            // A failed mark is not worth an error state: the feed is on screen
            // and correct, and the badge simply stays until the next visit.
            // Turning this into a banner would report a problem the reader has
            // no action for.
          });
          if (!cancelled) onUnreadCountChanged(0);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && err instanceof ApiError) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
    // `onUnreadCountChanged` and `absorb` are stable; depending on them would
    // refetch on every parent render and re-mark the watermark each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = () => {
    if (!cursor || busy) return;
    setBusy(true);
    api
      .alerts(cursor)
      .then((payload) => absorb(payload, true))
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  };

  const markAllRead = () => {
    if (!newest) return;
    void api.markAlertsSeen(newest).then(() => {
      onUnreadCountChanged(0);
      // Re-read rather than patching `unread` locally: the predicate is the
      // server's (decision 400), and a client that computed it here would be the
      // second implementation.
      void api.alerts().then((payload) => absorb(payload, false));
    });
  };

  const clearAll = () => {
    if (!newest) return;
    setConfirmingClear(false);
    void api.clearAlerts(newest).then(() => {
      onUnreadCountChanged(0);
      void api.alerts().then((payload) => absorb(payload, false));
    });
  };

  const dismiss = (alert: Alert) => {
    // Optimistic, and safe because the undo is one tap: the row visibly leaving
    // IS the feedback, which is the argument Manage fencers makes for removing
    // without a confirmation.
    setAlerts((prev) => (prev ? prev.filter((a) => a.id !== alert.id) : prev));
    setUndoable(alert);
    void api.hideAlert(alert.id).catch(() => {
      // Put it back rather than leaving the screen lying about what happened.
      setAlerts((prev) => (prev ? [alert, ...prev] : prev));
      setUndoable(null);
    });
  };

  const undo = () => {
    if (!undoable) return;
    const alert = undoable;
    setUndoable(null);
    void api.restoreAlert(alert.id).then(() => {
      void api.alerts().then((payload) => absorb(payload, false));
    });
  };

  if (failed && !alerts) {
    return (
      <div className={styles.screen}>
        <h1 className={styles.h1}>{t("notifications.heading")}</h1>
        <p className={styles.error}>{t("error.couldNotLoad")}</p>
      </div>
    );
  }

  // Nothing rendered while the first page is in flight. A spinner that resolves
  // in 80ms is a flash, and §6's rule is that motion is functional only — the
  // same call the three data views make.
  if (!alerts) return null;

  const groups = groupByDay(alerts, now ?? new Date());
  const empty = alerts.length === 0;

  return (
    <div className={styles.screen}>
      <div className={styles.head}>
        <h1 className={styles.h1}>{t("notifications.heading")}</h1>

        {/*
          PLURAL CHROME FOR PLURAL DATA (build rule 3): both verbs are controls
          over a list, so on an empty feed they are controls with no outcome —
          which reads as the app being broken rather than as there being nothing
          to read.
        */}
        {empty ? null : (
          <div className={styles.actions}>
            <button type="button" className={styles.action} onClick={markAllRead}>
              {t("notifications.markAllRead")}
            </button>
            <button
              type="button"
              className={styles.action}
              onClick={() => setConfirmingClear(true)}
            >
              {t("notifications.deleteAll")}
            </button>
          </div>
        )}
      </div>

      {/*
        A CONFIRMATION HERE AND NOT ON A SINGLE DISMISS, which is the same
        reasoning Manage fencers uses read in both directions. One row costs one
        tap to restore, so a dialog would train the reader to dismiss dialogs;
        "delete all" has no undo, so it is the one place a confirmation earns its
        interruption. The copy says what actually happens rather than asking "are
        you sure" — nothing leaves the record, it stops showing here.
      */}
      {confirmingClear ? (
        <div className={styles.confirm} role="alertdialog" aria-live="polite">
          <p className={styles.confirmText}>{t("notifications.deleteAllConfirm")}</p>
          <div className={styles.confirmActions}>
            <button type="button" className={styles.action} onClick={() => setConfirmingClear(false)}>
              {t("notifications.deleteAllCancel")}
            </button>
            <button type="button" className={styles.danger} onClick={clearAll}>
              {t("notifications.deleteAll")}
            </button>
          </div>
        </div>
      ) : null}

      {undoable ? (
        <p className={styles.undo} role="status">
          {t("notifications.dismissed")}{" "}
          <button type="button" className={styles.undoButton} onClick={undo}>
            {t("notifications.undo")}
          </button>
        </p>
      ) : null}

      {empty ? (
        /*
          TWO SENTENCES, the same shape Active's terminal empty state settled on
          (deviations #6): the first says there is nothing, the second says what
          would appear here. A one-line "Nothing yet." leaves a reader unable to
          tell an empty feed from a broken one.
        */
        <div className={styles.emptyState}>
          <p className={styles.empty}>{t("notifications.empty")}</p>
          <p className={styles.emptyDetail}>{t("notifications.emptyDetail")}</p>
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.key} className={styles.day}>
            <h2 className={styles.dayHeading}>
              {dayHeading(group.daysAgo, group.key, t, i18n.language)}
            </h2>
            <ul className={styles.rows}>
              {group.alerts.map((alert) => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  nameFormat={nameFormat}
                  onDismiss={() => dismiss(alert)}
                />
              ))}
            </ul>
          </section>
        ))
      )}

      {cursor ? (
        <button
          type="button"
          className={styles.more}
          onClick={loadMore}
          disabled={busy}
        >
          {busy ? t("notifications.loading") : t("notifications.more")}
        </button>
      ) : null}
    </div>
  );
}

/**
 * One line of the feed.
 *
 * THE WHOLE ROW IS A REAL ANCHOR where a destination exists, because build rule
 * 10 makes navigation a contract: cmd-click opens a tab, right-click offers "copy
 * link address", and the fragment scrolls natively. The dismiss button sits
 * OUTSIDE it — a button nested in an anchor is invalid HTML and, worse, a tap
 * that lands on either one depending on the pixel.
 */
function AlertRow({
  alert,
  nameFormat,
  onDismiss,
}: {
  alert: Alert;
  nameFormat: NotificationNameFormat;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  const chosen = alertSentence(alert, nameFormat);
  const { key, ordinal } = chosen;
  // The DE table in the reader's language rather than FTL's English heading
  // (decision 951).
  const values = withTableName(chosen, (l) => t(l.key, { count: l.count }));
  // `ordinal` is an i18next OPTION and not an interpolation, so it is spread
  // separately: passing it inside `values` would leak the word into any
  // sentence referencing it, and omitting it selects the cardinal plural, which
  // renders "21th" (decision 484's copy).
  const fact = t(`notifications.kind.${key}`, { ...values, ...(ordinal ? { ordinal: true } : {}) });
  // A team's entry leads with the reader's tracked members (decision 717).
  const members = teamMembers(alert, nameFormat, t("team.nameSeparator"));
  const sentence = members ? t("notifications.teamSentence", { members, sentence: fact }) : fact;
  const source = attributionSource(alert);
  const href = alertDestination(alert);

  const body = (
    <>
      <span className={styles.sentence}>
        {sentence}
        {source ? (
          <span className={styles.attribution}>
            {" "}
            {t("notifications.attribution", { source: t(`source.${source}`) })}
          </span>
        ) : null}
      </span>
      {/*
        `Ago` rather than a second relative-time implementation: it shares
        AsOf's own bucketing (decision 322), so the feed and the freshness
        footers cannot come to disagree about when "1 hour ago" becomes "2".
      */}
      <span className={styles.when}>
        <Ago at={alert.observed_at} />
      </span>
    </>
  );

  return (
    <li className={alert.unread ? `${styles.row} ${styles.rowNew}` : styles.row}>
      {/*
        THE MARKER IS REAL TEXT, not a coloured dot. Direction §7 requires
        staleness, status and identity markers to be perceivable non-visually,
        and "unread" is exactly that class of marker — a dot with an aria-label
        is a workaround where a word is the thing itself.
      */}
      {alert.unread ? <span className={styles.new}>{t("notifications.new")}</span> : null}

      {href ? (
        <Link className={styles.link} to={href}>
          {body}
        </Link>
      ) : (
        <span className={styles.link}>{body}</span>
      )}

      <button
        type="button"
        className={styles.dismiss}
        onClick={onDismiss}
        aria-label={t("notifications.dismissNamed")}
      >
        {t("notifications.dismiss")}
      </button>
    </li>
  );
}
